import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import apiClient from "../apiClient";
import { setActiveEntityId } from "../auth.js";

const emptyEntityForm = { Description: "", ActiveYesNo: 1 };
const emptyUserForm = { UserName: "", Password: "", FirstName: "", LastName: "", IsAdministrator: 0 };
const emptyGrantForm = { UserID: "", RoleName: "member" };
const parseApiError = (err, fallback) => err?.response?.data?.message || err?.message || fallback;

const Entities = () => {
  const [currentEntityId, setCurrentEntityId] = useState("");
  const [entities, setEntities] = useState([]);
  const [users, setUsers] = useState([]);
  const [entityUsers, setEntityUsers] = useState([]);
  const [entityForm, setEntityForm] = useState(emptyEntityForm);
  const [newEntity, setNewEntity] = useState({ Description: "" });
  const [newUser, setNewUser] = useState(emptyUserForm);
  const [grant, setGrant] = useState(emptyGrantForm);
  const [loading, setLoading] = useState(true);
  const [savingEntity, setSavingEntity] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const loadReferenceData = async (requestedEntityId) => {
    const entitiesRes = await apiClient.get("/entities");
    const entityRows = Array.isArray(entitiesRes.data) ? entitiesRes.data : [];
    const fallbackEntityId = requestedEntityId || entityRows[0]?.EntityID || "";
    const resolvedEntityId = entityRows.some((item) => String(item.EntityID) === String(fallbackEntityId))
      ? String(fallbackEntityId)
      : String(entityRows[0]?.EntityID || "");

    const selectedEntity = entityRows.find((item) => String(item.EntityID) === resolvedEntityId);

    setEntities(entityRows);
    setCurrentEntityId(resolvedEntityId);
    setEntityForm(
      selectedEntity
        ? {
            Description: selectedEntity.Description || "",
            ActiveYesNo: Number(selectedEntity.ActiveYesNo ? 1 : 0),
          }
        : emptyEntityForm
    );

    const [usersResult, entityUsersResult] = await Promise.allSettled([
      apiClient.get("/users"),
      resolvedEntityId ? apiClient.get("/entity-users", { params: { entityId: resolvedEntityId } }) : Promise.resolve({ data: [] }),
    ]);

    if (usersResult.status === "fulfilled") {
      const userRows = Array.isArray(usersResult.value.data) ? usersResult.value.data : [];
      setUsers(userRows);
      setGrant((prev) => ({
        ...prev,
        UserID: prev.UserID || (userRows[0] ? String(userRows[0].UserID) : ""),
      }));
    } else {
      setUsers([]);
      throw new Error(parseApiError(usersResult.reason, "Failed to load users."));
    }

    if (entityUsersResult.status === "fulfilled") {
      const accessRows = Array.isArray(entityUsersResult.value.data) ? entityUsersResult.value.data : [];
      setEntityUsers(accessRows);
    } else {
      setEntityUsers([]);
      throw new Error(parseApiError(entityUsersResult.reason, "Failed to load entity users."));
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError("");
      try {
        await loadReferenceData("");
      } catch (err) {
        setError(parseApiError(err, "Failed to load entities."));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const refreshSelectedEntity = async (entityId) => {
    setLoading(true);
    setError("");
    try {
      await loadReferenceData(entityId);
    } catch (err) {
      setError(parseApiError(err, "Failed to refresh entities."));
    } finally {
      setLoading(false);
    }
  };

  const handleEntitySelection = async (entityId) => {
    setCurrentEntityId(entityId);
    setActiveEntityId(entityId);
    await refreshSelectedEntity(entityId);
  };

  const handleCreateEntity = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    try {
      const payload = { Description: newEntity.Description.trim() };
      const res = await apiClient.post("/entities", payload);
      const createdEntityId = String(res.data?.id || "");
      setNewEntity({ Description: "" });
      await refreshSelectedEntity(createdEntityId);
      if (createdEntityId) setActiveEntityId(createdEntityId);
      setSuccess("Entity created.");
    } catch (err) {
      setError("Could not create entity.");
    }
  };

  const handleSaveEntity = async (e) => {
    e.preventDefault();
    if (!currentEntityId) return;
    setSavingEntity(true);
    setError("");
    setSuccess("");
    try {
      await apiClient.put(`/entities/${currentEntityId}`, {
        Description: entityForm.Description.trim(),
        ActiveYesNo: Number(entityForm.ActiveYesNo),
      });
      await refreshSelectedEntity(currentEntityId);
      setSuccess("Entity details saved.");
    } catch (err) {
      setError("Could not save entity details.");
    } finally {
      setSavingEntity(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    try {
      await apiClient.post("/users", newUser);
      setNewUser(emptyUserForm);
      await refreshSelectedEntity(currentEntityId);
      setSuccess("User created.");
    } catch (err) {
      setError("Could not create user.");
    }
  };

  const handleGrantAccess = async (e) => {
    e.preventDefault();
    if (!currentEntityId) return;
    setError("");
    setSuccess("");
    try {
      await apiClient.post("/entity-users", {
        EntityID: Number(currentEntityId),
        UserID: Number(grant.UserID),
        RoleName: grant.RoleName,
      });
      await refreshSelectedEntity(currentEntityId);
      setSuccess("Entity access updated.");
    } catch (err) {
      setError("Could not grant access.");
    }
  };

  const handleMembershipChange = async (entityUserId, updates) => {
    setError("");
    setSuccess("");
    try {
      const current = entityUsers.find((item) => item.EntityUserID === entityUserId);
      if (!current) return;
      await apiClient.put(`/entity-users/${entityUserId}`, {
        RoleName: updates.RoleName ?? current.RoleName,
        ActiveYesNo: updates.ActiveYesNo ?? current.ActiveYesNo,
      });
      await refreshSelectedEntity(currentEntityId);
      setSuccess("Entity access saved.");
    } catch (err) {
      setError("Could not update entity access.");
    }
  };

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Entities</h1>
          <p className="page-subtitle">
            <Link to="/">Home</Link> / Entities
          </p>
        </div>
      </header>

      {error && <p className="notice notice-error">{error}</p>}
      {success && <p className="notice notice-muted">{success}</p>}

      {loading ? (
        <p className="notice notice-muted">Loading entities...</p>
      ) : (
        <>
          <section className="panel">
            <div className="entity-toolbar">
              <div className="entity-toolbar-block">
                <label htmlFor="entities-select">Selected Entity</label>
                <select id="entities-select" value={currentEntityId} onChange={(e) => handleEntitySelection(e.target.value)}>
                  {entities.map((item) => (
                    <option key={item.EntityID} value={item.EntityID}>
                      {item.Description}
                    </option>
                  ))}
                </select>
              </div>

              <form className="entity-inline-form" onSubmit={handleCreateEntity}>
                <label htmlFor="entity-create-name">Add Entity</label>
                <div className="entity-inline-row">
                  <input
                    id="entity-create-name"
                    type="text"
                    placeholder="e.g. Family Office"
                    value={newEntity.Description}
                    onChange={(e) => setNewEntity({ Description: e.target.value })}
                  />
                  <button className="btn" type="submit">
                    Create Entity
                  </button>
                </div>
              </form>
            </div>
          </section>

          <section className="reports-grid page-cta">
            <section className="panel">
              <h2>Entity Details</h2>
              <form className="form" onSubmit={handleSaveEntity}>
                <label htmlFor="entity-description">Description</label>
                <input
                  id="entity-description"
                  type="text"
                  value={entityForm.Description}
                  onChange={(e) => setEntityForm((prev) => ({ ...prev, Description: e.target.value }))}
                />

                <label htmlFor="entity-active">Status</label>
                <select
                  id="entity-active"
                  value={String(entityForm.ActiveYesNo)}
                  onChange={(e) => setEntityForm((prev) => ({ ...prev, ActiveYesNo: Number(e.target.value) }))}
                >
                  <option value="1">Active</option>
                  <option value="0">Inactive</option>
                </select>

                <div className="form-actions">
                  <button className="btn" type="submit" disabled={!currentEntityId || savingEntity}>
                    Save Entity
                  </button>
                </div>
              </form>
            </section>

            <section className="panel">
              <h2>Create User</h2>
              <form className="form" onSubmit={handleCreateUser}>
                <input
                  type="text"
                  placeholder="Username"
                  value={newUser.UserName}
                  onChange={(e) => setNewUser((prev) => ({ ...prev, UserName: e.target.value }))}
                />
                <input
                  type="text"
                  placeholder="First name"
                  value={newUser.FirstName}
                  onChange={(e) => setNewUser((prev) => ({ ...prev, FirstName: e.target.value }))}
                />
                <input
                  type="text"
                  placeholder="Last name"
                  value={newUser.LastName}
                  onChange={(e) => setNewUser((prev) => ({ ...prev, LastName: e.target.value }))}
                />
                <input
                  type="password"
                  placeholder="Password"
                  value={newUser.Password}
                  onChange={(e) => setNewUser((prev) => ({ ...prev, Password: e.target.value }))}
                />
                <label className="checkbox-row">
                  <input
                    type="checkbox"
                    checked={Boolean(newUser.IsAdministrator)}
                    onChange={(e) => setNewUser((prev) => ({ ...prev, IsAdministrator: e.target.checked ? 1 : 0 }))}
                  />
                  <span>Administrator across all entities</span>
                </label>
                <button className="btn" type="submit">
                  Create User
                </button>
              </form>
            </section>

            <section className="panel">
              <h2>Grant Entity Access</h2>
              <form className="form" onSubmit={handleGrantAccess}>
                <select value={grant.UserID} onChange={(e) => setGrant((prev) => ({ ...prev, UserID: e.target.value }))}>
                  {users.map((item) => (
                    <option key={item.UserID} value={item.UserID}>
                      {item.UserName}
                    </option>
                  ))}
                </select>
                <select value={grant.RoleName} onChange={(e) => setGrant((prev) => ({ ...prev, RoleName: e.target.value }))}>
                  <option value="owner">owner</option>
                  <option value="member">member</option>
                </select>
                <button className="btn" type="submit" disabled={!currentEntityId || !grant.UserID}>
                  Grant Access
                </button>
              </form>
            </section>
          </section>

          <section className="panel page-cta">
            <h2>Users With Access To This Entity</h2>
            <div className="entity-access-list">
              {entityUsers.length === 0 ? (
                <p className="notice notice-muted">No users assigned yet.</p>
              ) : (
                entityUsers.map((item) => (
                  <article key={item.EntityUserID} className="entity-access-row">
                    <div>
                      <strong>{item.UserName}</strong>
                      <div className="resource-meta">
                        {item.FirstName} {item.LastName}
                      </div>
                    </div>
                    <div className="entity-access-actions">
                      <select
                        value={item.RoleName}
                        onChange={(e) => handleMembershipChange(item.EntityUserID, { RoleName: e.target.value })}
                      >
                        <option value="owner">owner</option>
                        <option value="member">member</option>
                      </select>
                      <select
                        value={String(item.ActiveYesNo)}
                        onChange={(e) => handleMembershipChange(item.EntityUserID, { ActiveYesNo: Number(e.target.value) })}
                      >
                        <option value="1">active</option>
                        <option value="0">inactive</option>
                      </select>
                    </div>
                  </article>
                ))
              )}
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default Entities;
